import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { version } from "../../package.json";
import { callEndpoint, useIsPermissionGranted } from "../util";
import { TbVersionsFilled } from "react-icons/tb";
import { IoLogOut } from "react-icons/io5";
import { TbLogs } from "react-icons/tb";
import { IoMdNotifications } from "react-icons/io";
import { UserPanelItem } from "./UserPanelItem";
import { Switch } from "@mui/material";
import { EMAIL_TO_USER } from "../definitions";
import {
  NotificationPreferences,
  NotificationPreferencesEndpoint,
  UpdateNotificationPreferencesEndpoint,
} from "@ckal-software/ckal-lib/dist/apps/kasse1";
import { OmitAC } from "@ckal-software/ckal-lib";
import { useNotifcation } from "../hooks/useNotification";
import {
  EMAIL_CASPER,
  EMAIL_CECILIE,
  UserInfoCAndC,
} from "@ckal-software/ckal-lib/dist/common";

const NotificationPreferencesItemPadding = "0px 8px 0px 24px";

interface UserPanelProps {
  onLogsClick(): void;
  onLogout(): void;
  userInfo: UserInfoCAndC;
  style?: CSSProperties;
}

export function UserPanel(props: UserPanelProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState<
    OmitAC<NotificationPreferences, "_id" | "user">
  >({
    otherAddedExpense: true,
    otherDidActionOnBehalfOfYou: true,
    otherRefunded: true,
    otherDeletedExpense: true,
    otherTransferredMoneyToYou: true,
  });

  const notify = useNotifcation();

  const fetchNotificationPreferences = useCallback(async () => {
    const [err, resultNotificationPreferences] = await callEndpoint(
      NotificationPreferencesEndpoint,
      undefined
    );

    if (err) {
      notify(`Der skete en fejl: ${err.error}`);
      return;
    }

    setNotificationPreferences({
      otherAddedExpense: resultNotificationPreferences.otherAddedExpense,
      otherDidActionOnBehalfOfYou:
        resultNotificationPreferences.otherDidActionOnBehalfOfYou,
      otherRefunded: resultNotificationPreferences.otherRefunded,
      otherDeletedExpense: resultNotificationPreferences.otherDeletedExpense,
      otherTransferredMoneyToYou:
        resultNotificationPreferences.otherTransferredMoneyToYou,
    });
  }, [notify]);

  useEffect(() => {
    fetchNotificationPreferences();
  }, [fetchNotificationPreferences]);

  async function updateNotificationPreferences(
    type: keyof typeof notificationPreferences,
    value: boolean
  ) {
    if (isUpdating) {
      return;
    }
    // Update optimistically
    setNotificationPreferences((curr) => ({ ...curr, [type]: value }));

    setIsUpdating(true);
    const [err] = await callEndpoint(UpdateNotificationPreferencesEndpoint, {
      type,
      value,
    });
    setIsUpdating(false);

    if (err) {
      notify(`Der skete en fejl: ${err.error}`);
      return;
    }

    fetchNotificationPreferences();
  }

  const isPermissionGranted = useIsPermissionGranted();

  const otherUser = useMemo(
    () =>
      EMAIL_TO_USER[
        props.userInfo.email === EMAIL_CASPER ? EMAIL_CECILIE : EMAIL_CASPER
      ],
    [props.userInfo.email]
  );

  return (
    <div style={{ display: "grid", rowGap: 16, ...props.style }}>
      <div
        style={{
          display: "grid",
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.1)",
          background: "white",
          gridAutoRows: "1fr",
        }}
      >
        <UserPanelItem
          text={`Version: ${version}`}
          rightNode={<TbVersionsFilled />}
          onClick={() => window.location.reload()}
        />
        <UserPanelItem
          text="Logs"
          onClick={props.onLogsClick}
          rightNode={<TbLogs />}
        />
        <UserPanelItem
          text="Log ud"
          onClick={props.onLogout}
          rightNode={<IoLogOut />}
          style={{ borderBottom: undefined }}
        />
      </div>
      <div
        style={{
          display: "grid",
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.1)",
          background: "white",
          gridAutoRows: "1fr",
        }}
      >
        <UserPanelItem
          text={`Notifikationer er konfigueret
            ${isPermissionGranted ? " korrekt" : " forkert"}`}
          rightNode={<IoMdNotifications />}
          style={{ color: "rgba(0,0,0,0.55)" }}
        />
        <UserPanelItem
          text={`Når ${otherUser} udfører på dine vegne`}
          rightNode={
            <Switch
              disabled={isUpdating}
              checked={notificationPreferences.otherDidActionOnBehalfOfYou}
            />
          }
          onClick={() =>
            updateNotificationPreferences(
              "otherDidActionOnBehalfOfYou",
              !notificationPreferences.otherDidActionOnBehalfOfYou
            )
          }
          style={{ padding: NotificationPreferencesItemPadding }}
        />
        <UserPanelItem
          text={`Når ${otherUser} tilføjer en udgift`}
          rightNode={
            <Switch
              disabled={isUpdating}
              checked={notificationPreferences.otherAddedExpense}
            />
          }
          onClick={() =>
            updateNotificationPreferences(
              "otherAddedExpense",
              !notificationPreferences.otherAddedExpense
            )
          }
          style={{ padding: NotificationPreferencesItemPadding }}
        />
        <UserPanelItem
          text={`Når ${otherUser} sletter en udgift`}
          rightNode={
            <Switch
              disabled={isUpdating}
              checked={notificationPreferences.otherDeletedExpense}
            />
          }
          onClick={() =>
            updateNotificationPreferences(
              "otherDeletedExpense",
              !notificationPreferences.otherDeletedExpense
            )
          }
          style={{
            padding: NotificationPreferencesItemPadding,
          }}
        />
        <UserPanelItem
          text={`Når ${otherUser} tilbagebetaler`}
          rightNode={
            <Switch
              disabled={isUpdating}
              checked={notificationPreferences.otherRefunded}
            />
          }
          onClick={() =>
            updateNotificationPreferences(
              "otherRefunded",
              !notificationPreferences.otherRefunded
            )
          }
          style={{
            padding: NotificationPreferencesItemPadding,
          }}
        />
        <UserPanelItem
          text={`Når ${otherUser} overfører til dig`}
          rightNode={
            <Switch
              disabled={isUpdating}
              checked={notificationPreferences.otherTransferredMoneyToYou}
            />
          }
          onClick={() =>
            updateNotificationPreferences(
              "otherTransferredMoneyToYou",
              !notificationPreferences.otherTransferredMoneyToYou
            )
          }
          style={{
            borderBottom: undefined,
            padding: NotificationPreferencesItemPadding,
          }}
        />
      </div>
    </div>
  );
}
